import { useState, useEffect, useCallback } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { Button } from "@/components/ui/button";
import { CheckCircle2, CreditCard, Loader2, Download } from "lucide-react";

export default function PublicInvoice() {
  const { token } = useParams();
  const { lang } = useLang();
  const [inv, setInv] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [paying, setPaying] = useState(false);
  const [params, setParams] = useSearchParams();

  const L = (es, en) => (lang === "es" ? es : en);

  const load = useCallback(() => {
    api.get(`/public/pay/${token}`).then((r) => setInv(r.data)).catch(() => setNotFound(true));
  }, [token]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const sid = params.get("paid");
    if (!sid) return;
    api.get(`/public/invoices/status/${sid}`).then((r) => {
      if (r.data.payment_status === "paid") load();
      setParams({}, { replace: true });
    }).catch(() => setParams({}, { replace: true }));
  }, [params, setParams, load]);

  const pay = async () => {
    setPaying(true);
    try {
      const { data } = await api.post(`/public/pay/${token}`, { origin_url: window.location.origin });
      if (data.already_paid) { load(); return; }
      if (data.checkout_url) window.location.href = data.checkout_url;
    } catch (err) {
      const { toast } = await import("sonner");
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setPaying(false); }
  };

  const downloadReceipt = () => {
    if (!inv) return;
    const esc = (s) => String(s ?? "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));
    const rows = (inv.items || []).map((it) => `<tr><td style="padding:6px 0;border-bottom:1px solid #eee;color:#475569">${esc(it.description)} × ${esc(it.quantity)}</td><td style="padding:6px 0;border-bottom:1px solid #eee;text-align:right;font-weight:600">$${(it.quantity * it.unit_price).toFixed(2)}</td></tr>`).join("");
    const paidDate = new Date(inv.paid_at || Date.now()).toLocaleDateString(lang === "es" ? "es" : "en");
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>${L("Recibo", "Receipt")} ${esc(inv.number)}</title>
<style>body{font-family:Arial,Helvetica,sans-serif;color:#0f172a;max-width:640px;margin:24px auto;padding:0 24px}h1{font-size:20px;margin:0}.muted{color:#94a3b8;font-size:12px}table{width:100%;border-collapse:collapse;margin-top:16px}.tot{display:flex;justify-content:space-between;padding:2px 0;color:#64748b}.grand{display:flex;justify-content:space-between;border-top:1px solid #cbd5e1;padding-top:8px;margin-top:6px;font-size:18px;font-weight:800}.ftr{margin-top:28px;border-top:1px solid #e2e8f0;padding-top:14px;text-align:center;color:#94a3b8;font-size:12px}</style></head>
<body>
<div style="display:flex;align-items:center;justify-content:space-between;border-bottom:1px solid #e2e8f0;padding-bottom:14px"><div style="display:flex;align-items:center;gap:16px">${inv.business_logo ? `<img src="${esc(inv.business_logo)}" style="height:70px;max-width:180px;object-fit:contain" />` : ""}<div><h1>${esc(inv.business_name || "")}</h1><div class="muted">${L("RECIBO", "RECEIPT")} · ${esc(inv.number)}</div></div></div><div style="background:#10b981;color:#fff;font-weight:800;padding:6px 14px;border-radius:6px">${L("PAGADO", "PAID")}</div></div>
<p style="margin-top:16px">${L("Recibido de", "Received from")}: <b>${esc(inv.client_name)}</b></p>
<p class="muted">${L("Fecha de pago", "Payment date")}: ${esc(paidDate)}</p>
<table><tbody>${rows}</tbody></table>
<div style="margin-top:16px"><div class="tot"><span>${L("Subtotal", "Subtotal")}</span><span>$${(inv.subtotal || 0).toFixed(2)}</span></div><div class="tot"><span>${L("Impuesto", "Tax")} (${esc(inv.tax_rate)}%)</span><span>$${(inv.tax || 0).toFixed(2)}</span></div><div class="grand"><span>${L("Total pagado", "Total paid")}</span><span>$${(inv.total || 0).toFixed(2)}</span></div></div>
<div class="ftr">${L("Gracias por tu pago", "Thank you for your payment")}</div>
<scr` + `ipt>window.onload=function(){setTimeout(function(){window.print();},400);}</scr` + `ipt>
</body></html>`;
    const w = window.open("", "_blank");
    if (w) { w.document.open(); w.document.write(html); w.document.close(); }
  };

  if (notFound) return <div className="flex min-h-screen items-center justify-center bg-slate-100 p-6 text-center text-slate-500" data-testid="pay-not-found">{L("Factura no encontrada.", "Invoice not found.")}</div>;
  if (!inv) return <div className="flex min-h-screen items-center justify-center bg-slate-100"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  const paid = inv.status === "paid";

  return (
    <div className="min-h-screen bg-slate-100 py-8 px-4" data-testid="public-invoice-page">
      <div className="mx-auto max-w-md overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="flex items-center gap-3 border-b border-slate-100 bg-secondary px-6 py-5">
          {inv.business_logo ? <img src={inv.business_logo} alt="" className="h-10 w-auto max-w-[120px] object-contain" /> : null}
          <div>
            <p className="font-head text-lg font-extrabold text-white">{inv.business_name}</p>
            <p className="text-xs text-slate-300">{inv.number}</p>
          </div>
        </div>
        <div className="px-6 py-6">
          <p className="text-sm text-slate-500">{L("Facturar a", "Bill to")}: <b className="text-secondary">{inv.client_name}</b></p>
          <table className="mt-4 w-full text-sm">
            <tbody>
              {(inv.items || []).map((it, i) => (
                <tr key={i} className="border-b border-slate-100">
                  <td className="py-2 text-slate-600">{it.description} × {it.quantity}</td>
                  <td className="py-2 text-right font-semibold">${(it.quantity * it.unit_price).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="mt-4 rounded-sm border border-slate-200 bg-slate-50 p-4 space-y-1.5 text-sm" data-testid="pay-breakdown">
            <div className="flex justify-between text-slate-600"><span>{L("Servicio", "Service")}</span><span data-testid="pay-subtotal">${(inv.subtotal || 0).toFixed(2)}</span></div>
            <div className="flex justify-between text-slate-600"><span>{L("Impuesto", "Tax")} ({inv.tax_rate}%)</span><span data-testid="pay-tax">${(inv.tax || 0).toFixed(2)}</span></div>
            <div className="flex justify-between border-t border-slate-200 pt-2 font-head text-xl font-extrabold text-secondary"><span>{L("Total a pagar", "Total to pay")}</span><span data-testid="pay-total">${(inv.total || 0).toFixed(2)}</span></div>
          </div>
          {inv.due_date && <p className="mt-2 text-xs text-slate-400">{L("Vence", "Due")}: {inv.due_date}</p>}

          {paid ? (
            <div className="mt-6 space-y-3" data-testid="pay-success">
              <div className="flex items-center justify-center gap-2 rounded-sm bg-accent/10 py-3 text-accent">
                <CheckCircle2 className="h-5 w-5" /> <span className="font-bold">{L("Factura pagada. ¡Gracias!", "Invoice paid. Thank you!")}</span>
              </div>
              <Button onClick={downloadReceipt} variant="outline" className="w-full rounded-sm border-accent text-accent hover:bg-accent/5" data-testid="download-receipt-btn">
                <Download className="mr-2 h-4 w-4" />{L("Descargar recibo", "Download receipt")}
              </Button>
            </div>
          ) : (
            <Button onClick={pay} disabled={paying} className="mt-6 w-full rounded-sm bg-primary py-6 text-base hover:bg-orange-600" data-testid="pay-now-btn">
              {paying ? <Loader2 className="mr-2 h-5 w-5 animate-spin" /> : <CreditCard className="mr-2 h-5 w-5" />}
              {L("Pagar ahora", "Pay now")} ${(inv.total || 0).toFixed(2)}
            </Button>
          )}
          <p className="mt-3 text-center text-xs text-slate-400">{L("Pago seguro procesado con Stripe", "Secure payment processed by Stripe")}</p>
        </div>
      </div>
    </div>
  );
}
